<template>
  <div class="relative flex flex-col space-y-4">
    <FiltersStageButton
      :title="$t('search.filters.airlines')"
      :subtitle="selectedAirlinesText"
      @click="emit('onStageChange', filtersConfig.FILTER_STAGES.AIRLINES)"
    />
    <section class="space-y-4">
      <TransferFilters
        :state="state"
        :offers="offers"
        :limits="limits.transferDuration"
        @onTransferFilterChange="emit('onTransferFilterChange', $event)"
        @onNightTransferFilterChange="
          emit('onNightTransferFilterChange', $event)
        "
      />
      <TimeFilters
        :routes="routes"
        :state="state.flights"
        @onFlightFilterChange="onFlightFilterChange"
      />
      <RangeFilter
        v-if="
          limits.flightDurationRange[1] !== 0 &&
          limits.flightDurationRange[0] !== limits.flightDurationRange[1]
        "
        :title="t('search.filters.flightDuration')"
        :state="state.flights[0].duration"
        :limits="limits.flightDurationRange"
        class="bg-background-neutral-secondary space-y-4 rounded-2xl p-4"
        @onUpdate="emit('onFlightDurationUpdate', $event)"
      >
        <template #label="{ label }">
          <span>{{ offersFilterLib.formatDurationLabel(label) }}</span>
        </template>
      </RangeFilter>
      <PriceFilter
        :state="state.prices"
        :limits="limits.prices"
        @onPriceUpdate="emit('onPriceUpdate', $event)"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import type { flightEntity } from 'freedom-shared';
  import type {
    FilterState,
    FlightFilterState,
  } from '@aviata/offers-filters-fns';
  import type { Dict } from '@aviata/platform-types';

  import { offersFilterLib } from '@/widgets/offer-filters';

  import { TransferFilters } from '@/features/transfer-filters';
  import { TimeFilters } from '@/features/time-filter';
  import { PriceFilter } from '@/features/price-filter';
  import type { searchOffersTypes } from '@/features/search-offers-filter';

  import { RangeFilter } from '@/shared/ui';

  import { filtersConfig, type FilterStage } from '../config';
  import FiltersStageButton from './FiltersStageButton.vue';

  const emit = defineEmits<{
    onStageChange: [stageName: FilterStage];
    onPriceUpdate: [prices: number[]];
    onTransferFilterChange: [flightFilterState: Partial<FlightFilterState>];
    onNightTransferFilterChange: [isWithoutNightTransfer: boolean];
    onFlightDurationUpdate: [flightDuration: number[]];
    onFlightFilterChange: [
      flightFilterState: Partial<FlightFilterState>,
      flightNumber: number,
    ];
  }>();

  const props = defineProps<{
    state: FilterState;
    routes: searchOffersTypes.MappedFlightPoint[];
    limits: searchOffersTypes.FilterLimits;
    offers: flightEntity.types.MappedOffer[];
    airlines: Dict.Entity['airlines'];
  }>();

  const { t } = useI18n();

  const selectedAirlinesText = computed(() => {
    const { airlines, isAllAirlines } = props.state;
    const selectedAirlines = Object.keys(airlines).filter(
      iata => airlines[iata],
    );
    return !isAllAirlines
      ? selectedAirlines.map(iata => props.airlines[iata]?.name).join(', ')
      : t('search.filters.all');
  });

  const onFlightFilterChange = (
    flightFilterState: Partial<FlightFilterState>,
    flightNumber: number,
  ) => emit('onFlightFilterChange', flightFilterState, flightNumber);
</script>
